import { TextField, Typography } from '../../primitives/index.js'
import type { EntityOrElement } from '@tunasong/schemas'
import React from 'react'
import { TagList } from '../../tags/index.js'
import { capitalize, isSystemTag } from '@tunasong/models'
import { Divider } from '../../layout/divider.js'

export interface ElementFormProps<T> {
  className?: string
  element?: Partial<T>
  onChange(element: Partial<T>): void
  onClose?(): void
}

const fieldStyle = {
  marginTop: 8,
  marginBottom: 8,
}

export const ElementForm = <T extends EntityOrElement = EntityOrElement>(props: ElementFormProps<T>) => {
  const { element, onClose, onChange } = props

  const setField =
    <K extends keyof T>(key: K) =>
    (value: T[K]) => {
      const val: Partial<T> = { [key]: value } as never
      onChange(val)
    }

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter' && onClose) {
      ev.preventDefault()
      onClose()
    }
  }

  const handleTextField =
    (key: keyof T) =>
    ({ target: { value } }: { target: { value: string } }) =>
      setField(key)(value as never)

  if (!element) {
    return null
  }
  const { name, description, tags, type } = element

  // System tags (sys:*, legacy 'tunabrain') are backend-managed; hide them from
  // the user-editable chip list, but preserve them on save so the user can't
  // accidentally drop them by adding/removing a regular tag.
  const allTags = tags ?? []
  const systemTags = allTags.filter(t => isSystemTag(t))
  const visibleTags = allTags.filter(t => !isSystemTag(t))
  const handleTagsChange = (next: string[]) => {
    setField('tags')([...systemTags, ...next] as never)
  }

  return (
    <>
      <Typography variant="caption">Type: {capitalize(type ?? 'Unknown')}</Typography>
      <TextField
        size="small"
        style={fieldStyle}
        fullWidth={true}
        label="Name"
        defaultValue={name ?? ''}
        autoFocus={true}
        onKeyDown={handleKeyDown}
        onChange={handleTextField('name')}
      />
      <TextField
        size="small"
        style={fieldStyle}
        fullWidth={true}
        label="Description"
        multiline={true}
        defaultValue={description ?? ''}
        onChange={handleTextField('description')}
      />
      <Divider>
        <Typography variant="caption">Tags</Typography>
      </Divider>
      <TagList tags={visibleTags} onChange={handleTagsChange} />
    </>
  )
}

export default ElementForm
